import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { fromEvent, Observable, Subject, catchError, map, tap } from 'rxjs';

import { saveAs } from 'file-saver';

// #region Custom Enumerations

// #region Download Format Enum

export enum DownloadFormat {
    PDF = 0,
    Excel = 1,
    ZIP = 2,
}

// #endregion

// #region Http Method Enum

export enum HttpMethod {
    GET = 0,
    POST = 1,
}

// #endregion

// #endregion

@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    downloadSubject: Subject<Response> = new Subject<Response>();

    constructor(private http: HttpClient) {}

    // #region Retrieve Download Service Call

    // tslint:disable-next-line: no-any
    RetrieveDownload(
        httpMethod: HttpMethod,
        ServiceURL: string,
        // tslint:disable-next-line: no-any
        Parameters: any,
        Format: DownloadFormat
    ): Observable<HttpResponse<Blob> | Event> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'Journey',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const queryString = Object.keys(Parameters)
                .reduce((prev: string[], cur: string) => {
                    prev.push(`${encodeURIComponent(cur)}=${encodeURIComponent(Parameters[cur] || '')}`);

                    return prev;
                }, [])
                .join('&');

            const url = `${ServiceURL}?${queryString}`;

            responseObs = this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(ServiceURL, Parameters, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                if (blob) {
                    saveAs(blob, filename);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Retrieve File Service Call

    RetrieveFile(
        httpMethod: HttpMethod,
        FileURL: string,
        Format: DownloadFormat,
        Filename?: string
    ): Observable<HttpResponse<Blob> | Event> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'DELTA',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const url = `${FileURL}`;

            responseObs = this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(FileURL, null, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = Filename || 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                if (blob) {
                    saveAs(blob, filename);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion
}
